import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { productsApi, stockApi } from '../api/client';
import Card from '../components/Card';
import Button from '../components/Button';
import ConfirmDialog from '../components/ConfirmDialog';

// Функция форматирования количества: целые числа без десятичной части, дробные с одним знаком
const formatQty = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0';
  return num % 1 === 0 ? num.toString() : num.toFixed(1);
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: product, isLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      const response = await productsApi.getById(id);
      return response.data;
    },
  });

  const { data: stock } = useQuery({
    queryKey: ['stock', id],
    queryFn: async () => {
      const response = await stockApi.getStock(id);
      return response.data;
    },
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => productsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['products']);
      navigate('/products');
    },
    onError: (error) => {
      console.error('Ошибка при удалении товара:', error);
      setShowDeleteConfirm(false);
    },
  });

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    deleteMutation.mutate();
    setShowDeleteConfirm(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="text-center py-8">Загрузка...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-4">
        <div className="text-center py-8">Товар не найден</div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <button
          onClick={() => navigate('/products')}
          className="text-blue-600 mb-4"
        >
          ← Назад
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h1>
      </div>

      <Card>
        <div className="space-y-3">
          <div>
            <span className="text-sm text-gray-500">QR-код</span>
            <p className="text-base font-mono">{product.qr_code}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Цена</span>
            <p className="text-xl font-bold text-blue-600">
              {parseFloat(product.price).toFixed(2)} ₸
            </p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Текущий остаток</span>
            <p className="text-2xl font-bold text-gray-900">
              {formatQty(stock?.total_stock ?? stock?.stock ?? 0)}
            </p>
          </div>
          {product.description && (
            <div>
              <span className="text-sm text-gray-500">Описание</span>
              <p className="text-base">{product.description}</p>
            </div>
          )}
          {product.product_code && (
            <div>
              <span className="text-sm text-gray-500">Код товара</span>
              <p className="text-base font-mono">{product.product_code}</p>
            </div>
          )}
          {product.item_code && (
            <div>
              <span className="text-sm text-gray-500">Артикул</span>
              <p className="text-base font-mono">{product.item_code}</p>
            </div>
          )}
          {product.lot_number && (
            <div>
              <span className="text-sm text-gray-500">Номер партии</span>
              <p className="text-base">{product.lot_number}</p>
            </div>
          )}
          {product.production_date && (
            <div>
              <span className="text-sm text-gray-500">Дата производства</span>
              <p className="text-base">{new Date(product.production_date).toLocaleDateString('ru-RU')}</p>
            </div>
          )}
          {product.expiry_date && (
            <div>
              <span className="text-sm text-gray-500">Срок годности</span>
              <p className="text-base">{new Date(product.expiry_date).toLocaleDateString('ru-RU')}</p>
            </div>
          )}
          {product.diameter !== null && product.diameter !== undefined && (
            <div>
              <span className="text-sm text-gray-500">Диаметр</span>
              <p className="text-base">{product.diameter}</p>
            </div>
          )}
          {product.shelf_life_days !== null && product.shelf_life_days !== undefined && (
            <div>
              <span className="text-sm text-gray-500">Срок использования</span>
              <p className="text-base">{product.shelf_life_days} {product.shelf_life_days === 1 ? 'день' : product.shelf_life_days < 5 ? 'дня' : 'дней'}</p>
            </div>
          )}
        </div>
      </Card>

      <div className="space-y-3 mt-4">
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={() => navigate(`/stock-in?product_id=${id}`)}
        >
          ➕ Добавить на склад
        </Button>
        <Button
          variant="success"
          size="lg"
          className="w-full"
          onClick={() => navigate(`/china-orders?mode=create&product_id=${id}`)}
        >
          🇨🇳 Заказ из Китая
        </Button>
        <Button
          variant="success"
          size="lg"
          className="w-full"
          onClick={() => navigate(`/sales?product_id=${id}`)}
        >
          💰 Продать
        </Button>
        <Button
          variant="secondary"
          size="lg"
          className="w-full"
          onClick={() => navigate(`/products/edit/${id}`)}
        >
          ✏️ Редактировать
        </Button>
        <Button
          variant="danger"
          size="lg"
          className="w-full"
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
        >
          {deleteMutation.isPending ? 'Удаление...' : '🗑️ Удалить товар'}
        </Button>
      </div>

      {deleteMutation.error && (
        <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
          {deleteMutation.error.response?.data?.error || 'Ошибка при удалении товара'}
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Подтвердите удаление"
        message={`Вы уверены, что хотите удалить товар "${product?.name}"? Это действие нельзя отменить.`}
        confirmText="Удалить"
        cancelText="Отмена"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default ProductDetail;

